import { useMemo, useState, ReactNode, useEffect, useCallback } from 'react'
import debounce from 'lodash/debounce'
import { ApolloQueryResult, useQuery } from '@apollo/client'
import { Row, Col, Dropdown } from 'react-bootstrap'
import type { IFilterData } from '@rankmi/rankmi-table/dist/interfaces/filterProps'
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import {
  TDataCourseUsers,
  COURSE_USERS,
  QueryCourseUsersArgs,
  QueryCourseAllArgs,
  TDataCourse,
  Course,
} from 'graphqlAPI'
import { IAreaModel } from 'models/area.model'
import { sortTableEnum } from 'enum/sortTable.enum'
import { applyFilters } from 'helpers/filterData.helper'
import { useTranslationRankmi } from 'helpers/translations.helper'
import { CourseStateType, EnrolState } from 'enum'
import * as fromCoursesActions from 'store/actions/courses.action'
import { useAccess } from 'contexts/AccessContext'
import { SuccessToast } from 'components/ui/toast/toastsTemplates'
import { Columns } from '../../constants'
import { prefix } from './constants'
import Table from './table'
import { DownloadCertificates } from './options/DownloadCertificates'
import SendEmail from './components/send-emails/sendEmails'
import DeleteSelection from './components/DeleteSelection'
import InviteParticipants from './components/InviteParticipants'
import MassiveImport from './components/MassiveImport'
import styles from './Courses.module.scss'
import UploadButton from './components/UploadButton'
import ResetSelection from './components/ResetSelection'
import NormativeIncompleteParticipantsBanner from './components/NormativeIncompleteParticipantsBanner'

interface CoursesProps {
  courseId: string
  area?: IAreaModel | null
  filterData?: IFilterData
  filters?: ReactNode
  filtersView?: ReactNode
  course?: Course
  refetchCourse: (
    variables?: Partial<QueryCourseAllArgs> | undefined
  ) => Promise<ApolloQueryResult<TDataCourse>>
  loadingCourse: boolean
}

export const Users = ({
  courseId,
  area,
  filterData,
  filters,
  filtersView,
  course,
  loadingCourse,
  refetchCourse,
}: CoursesProps) => {
  const tRelative = useTranslationRankmi({ prefix })
  const [text, setText] = useState('')
  const { t } = useTranslation()

  const [perPage, setPerPage] = useState(10)
  const [page, setPage] = useState(1)
  const [sorts, setSorts] = useState<string[]>([])

  const [selected, setSelected] = useState<string[]>([])
  const [allSelected, setAllSelected] = useState(false)

  const [showInviteModal, setShowInviteModal] = useState(false)
  const [showImportParticipantsModal, setShowImportParticipantsModal] =
    useState(false)
  const [errorOnUpload, setErrorOnUpload] = useState<React.ReactNode>(null)

  const { can, canAccessCourse, access } = useAccess()
  const hasCourseScope = canAccessCourse(Number(courseId))
  const canManageParticipants = hasCourseScope && can('manageParticipants')
  const canManageProgress = hasCourseScope && can('manageProgress')
  const canSendBulkEmail = hasCourseScope && access.canSendCourseBulkEmail()
  const canDownloadReports =
    hasCourseScope && access.canDownloadReportWithViewAccess()

  const hasBulkActions =
    canManageParticipants ||
    canManageProgress ||
    canSendBulkEmail ||
    canDownloadReports

  const dispatch = useDispatch()

  const resetSelected = useCallback(() => {
    setSelected([])
    setAllSelected(false)
  }, [])

  const handleResetTable = useCallback(() => {
    setText('')
    resetSelected()
  }, [resetSelected])

  const areaId = area?.id?.toString()

  const ransackQueryGroup = useMemo(() => {
    if (filterData) {
      const newData = [...(filterData.data || [])]

      const newG = applyFilters({
        ...filterData,
        data: newData,
      })

      const finalStatePendingEq = newData.find(
        ({ input, compareTo }) =>
          input === Columns.finalState &&
          compareTo === String(EnrolState.Pending)
      )

      if (finalStatePendingEq) {
        const { input, operator } = finalStatePendingEq
        const buildKey = (o) => `${input}_${o}`
        // When EnrolState.Pending is selected, we need to add the other states
        if (input && operator === 'eq') {
          const key = buildKey(operator)
          for (const filter of newG) {
            if (key in filter) {
              const keyNull = buildKey('null')
              filter.m = 'or'
              filter[keyNull] = true
            }
          }
        }
      }
      return newG
    }
  }, [filterData])

  useEffect(() => {
    setPage(1)
  }, [sorts, text.length, ransackQueryGroup])

  type Args = QueryCourseUsersArgs

  const variables = useMemo<Args>(
    () => ({
      courseId: courseId,
      areaId,
      perPage,
      page,
      q:
        sorts.length || text.length || ransackQueryGroup?.length
          ? {
              sorts: sorts,
              user_full_name_or_user_identifier_cont: text,
              g: ransackQueryGroup,
            }
          : undefined,
    }),
    [courseId, areaId, perPage, page, sorts, text, ransackQueryGroup]
  )

  const [show, setShow] = useState(false)

  const setTextDebounced = useMemo(
    () => debounce(setText, 1000, { maxWait: 2000 }),
    [setText]
  )

  const buildUserSelectedQuery = useCallback(
    (opts?: { type?: 'delete' | 'select' }) => {
      const { type = 'select' } = { ...opts }
      const ids = selected.map((user) => +user)
      // This is for the case of deleting all selected users
      // Only the selected users will be deleted
      const selectDefault = type === 'delete' ? [0] : []
      return {
        user_id_in: allSelected ? [] : ids.length ? ids : selectDefault,
        ...(allSelected && { user_id_not_in: ids }),
      }
    },
    [selected, allSelected]
  )

  const userSelectedQuery = useMemo(
    () => buildUserSelectedQuery({ type: 'select' }),
    [buildUserSelectedQuery]
  )

  const userSelectedDeleteQuery = useMemo(
    () => buildUserSelectedQuery({ type: 'delete' }),
    [buildUserSelectedQuery]
  )

  const handleDownload = () => {
    dispatch(
      fromCoursesActions.Actions.coursesIndividualDownload({
        courseId: +courseId,
        filters: {
          ...userSelectedQuery,
          g: ransackQueryGroup,
        },
      })
    )
  }

  const handleBasicReportDownload = () => {
    dispatch(
      fromCoursesActions.Actions.coursesBasicReportDownload({
        courseId: +courseId,
        filters: {
          ...userSelectedQuery,
          g: ransackQueryGroup,
        },
      })
    )
  }

  const handleGeneralReportDownload = () => {
    dispatch(
      fromCoursesActions.Actions.coursesIndividualDownload({
        courseId: +courseId,
        filters: {
          ...userSelectedQuery,
          g: ransackQueryGroup,
        },
        onlyGeneralInfo: true,
      })
    )
  }

  const handleInvite = () => {
    setShowInviteModal(true)
  }

  const handleMassiveInvite = () => {
    setShowImportParticipantsModal(true)
  }

  const {
    data,
    loading: loadingUsers,
    refetch: refetchCourseUsers,
  } = useQuery<TDataCourseUsers, Args>(COURSE_USERS, {
    variables,
    nextFetchPolicy: 'network-only',
    fetchPolicy: 'network-only',
  })

  const refetchData = useCallback(async () => {
    await Promise.all([refetchCourseUsers(), refetchCourse()])
  }, [refetchCourse, refetchCourseUsers])

  const loadingTable = loadingCourse || loadingUsers

  const { courseUsers } = { ...data }

  const { nodes = [], nodesCount } = useMemo(() => {
    const nodesCount = courseUsers?.nodesCount || 0
    const nodes = [...(courseUsers?.nodes || [])]

    return {
      nodes: nodes.length ? nodes : [],
      nodesCount,
    }
  }, [courseUsers])

  const showToast = () => {
    SuccessToast(t(`administration.monitor.resetSelected.toasts.success`))
  }

  const selectedCount = !allSelected
    ? selected.length
    : nodesCount - selected.length

  const selectedUsers = useMemo(() => {
    const selectedSet = new Set(selected.map((id) => `${id}`))
    return nodes.filter(({ userId }) =>
      allSelected ? !selectedSet.has(userId) : selectedSet.has(userId)
    )
  }, [allSelected, nodes, selected])

  const imparted = course?.state === CourseStateType.Imparted
  const inactive = course?.state === CourseStateType.Inactive
  const participantActionsDisabled = imparted || inactive

  return (
    <div>
      <NormativeIncompleteParticipantsBanner courseId={courseId} />
      <Row>
        <Col xs={12}>
          <Row>
            {filters && <Col>{filters}</Col>}
            <Col>
              {hasBulkActions && (
                <Dropdown
                  id="dropdown-manage"
                  className={`${styles.dropdownManage}`}
                  alignRight
                  show={show}
                  onToggle={() => setShow(!show)}
                >
                  <Dropdown.Toggle
                    id="dropdown-manage-toggle"
                    bsPrefix="noCaret"
                    variant={'secondary'}
                  >
                    {tRelative('actions.label')}{' '}
                    <FontAwesomeIcon icon={faChevronDown} />
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    {canManageParticipants && (
                      <>
                        <Dropdown.Header>
                          {tRelative('actions.sections.participantManagement')}
                        </Dropdown.Header>
                        <Dropdown.Item
                          className={styles.buttonTitle}
                          onClick={() => handleInvite()}
                          disabled={participantActionsDisabled}
                        >
                          {tRelative(
                            'actions.options.inviteParticipantsManually'
                          )}
                        </Dropdown.Item>
                        <Dropdown.Item
                          className={styles.buttonTitle}
                          onClick={() => handleMassiveInvite()}
                          disabled={participantActionsDisabled}
                        >
                          {tRelative(
                            'actions.options.importParticipantsManually'
                          )}
                        </Dropdown.Item>
                        <DeleteSelection
                          className={styles.buttonTitle}
                          searchText={text}
                          count={selectedCount}
                          q={userSelectedDeleteQuery}
                          courseId={courseId}
                          selectedUsers={selectedUsers}
                          ransackQueryGroup={ransackQueryGroup}
                          disabled={participantActionsDisabled}
                          onDeleted={() => {
                            resetSelected()
                            refetchData()
                          }}
                        />
                      </>
                    )}
                    {(canManageProgress || canSendBulkEmail) && (
                      <Dropdown.Header>
                        {tRelative('actions.sections.resultsManagement')}
                      </Dropdown.Header>
                    )}
                    {canManageProgress && (
                      <>
                        <ResetSelection
                          className={styles.buttonTitle}
                          searchText={text}
                          count={selectedCount}
                          q={userSelectedDeleteQuery}
                          courseId={courseId}
                          selectedUsers={selectedUsers}
                          ransackQueryGroup={ransackQueryGroup}
                          disabled={participantActionsDisabled}
                          onDeleted={() => {
                            resetSelected()
                            refetchData()
                            showToast()
                          }}
                        />
                        <UploadButton
                          className={styles.buttonTitle}
                          refetchCourseUsers={refetchCourseUsers}
                          disabled={participantActionsDisabled}
                        />
                      </>
                    )}
                    {canSendBulkEmail && (
                      <SendEmail
                        filterData={filterData}
                        searchText={text}
                        disabled={inactive}
                        {...{ selected, allSelected, selectedCount }}
                      />
                    )}
                    {canDownloadReports && (
                      <>
                        <Dropdown.Header>
                          {tRelative('actions.sections.reportDownload')}
                        </Dropdown.Header>
                        {course && (
                          <DownloadCertificates
                            {...{
                              course: course,
                              nodesCount,
                              selected,
                              allSelected,
                              q: ransackQueryGroup as [],
                              search: text,
                              courseActionsDisabled: inactive,
                            }}
                          />
                        )}
                        <Dropdown.Item
                          className={styles.buttonTitle}
                          onClick={() => handleGeneralReportDownload()}
                          disabled={inactive || selectedCount === 0}
                        >
                          {tRelative('actions.options.generalReport', {
                            count: selectedCount,
                          })}
                        </Dropdown.Item>
                        <Dropdown.Item
                          className={styles.buttonTitle}
                          onClick={() => handleBasicReportDownload()}
                          disabled={inactive}
                        >
                          {tRelative('actions.options.legacy')}
                        </Dropdown.Item>
                        <Dropdown.Item
                          className={styles.buttonTitle}
                          onClick={() => handleDownload()}
                          disabled={inactive}
                        >
                          {tRelative('actions.options.detailedReportNewLower')}
                        </Dropdown.Item>
                      </>
                    )}
                  </Dropdown.Menu>
                </Dropdown>
              )}
            </Col>
            {filtersView && <Col xs={12}>{filtersView}</Col>}
          </Row>
        </Col>
        <Col xs={12}>
          <Table
            setPage={(newPage: number) => {
              setPage(newPage)
            }}
            setSearch={(newSearch: string) => {
              setTextDebounced(newSearch)
            }}
            setSort={({ field, order }) => {
              setSorts(order ? [`${field} ${sortTableEnum[order]}`] : [])
            }}
            setPerPage={(newPerPage: number) => {
              setPerPage(newPerPage)
            }}
            setSelectedRows={(newSelectedRows) => {
              setSelected(newSelectedRows)
            }}
            selectedRows={selected}
            setAllSelected={(newAllSelected: boolean) => {
              setAllSelected(newAllSelected)
            }}
            loading={loadingTable}
            list={nodes}
            total={nodesCount}
            currentPage={page}
            course={course}
            refetch={refetchData}
          />
        </Col>
      </Row>
      <InviteParticipants
        show={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        courseId={courseId}
        resetFilters={handleResetTable}
        refetch={refetchData}
      />
      <MassiveImport
        show={showImportParticipantsModal}
        courseId={courseId}
        setShow={setShowImportParticipantsModal}
        setError={setErrorOnUpload}
        error={errorOnUpload}
      />
    </div>
  )
}
